import { useEffect } from 'react'
import { Logger } from '@/components/Logger'
import { useGetUserSelf } from '@/hooks/v1'
import { getPLForSelfByAccountQueryKey } from '@/hooks/v1/hooks/useGetPLForSelfByAccount'
import type { PLAccounts } from '@/hooks/v1/types/PLAccounts'
import type { PLResponse } from '@/hooks/v1/types/PLResponse'
import { queryClient } from '@/services/api'
import { subscribeRealtime } from '@/services/api/realtimeSubscription'
import { isWebSocketErrorMessage } from './useWebSocketStatus'

export const useUserPnlSubscription = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data: userSelfData } = useGetUserSelf()
  const userId = userSelfData?.data?.id

  useEffect(() => {
    if (!enabled || !userId) return

    return subscribeRealtime(
      {
        onMessage: (message: unknown) => {
          if (isWebSocketErrorMessage(message)) {
            Logger.error('DEBUG WS PNL: Received error message:', message)
            return
          }

          if (!message || typeof message !== 'object' || !('pnl' in message)) return
          const pnl = message.pnl
          if (!pnl || typeof pnl !== 'object') return
          const accounts = (pnl as Record<string, PLAccounts>)[userId]
          if (!Array.isArray(accounts)) return

          // Each notification is a full report, including removed accounts and empty reports.
          queryClient.setQueryData<PLResponse>(getPLForSelfByAccountQueryKey(), (previous) =>
            previous ? { ...previous, data: accounts } : undefined,
          )
        },
      },
      {
        key: `pnl:${userId}`,
        path: '/pnl/byuser',
        subscribeData: { subscribe: [userId] },
        unsubscribeData: { unsubscribe: [userId] },
      },
    )
  }, [enabled, userId])
}
